import { cn } from 'src/lib/utils';

// ----------------------------------------------------------------------

interface SpinnerProps {
  /** Size the ring with a utility class, e.g. "size-14". Required — the ring has no intrinsic size. */
  className?: string;
  /** Optional content framed by the ring (e.g. a workspace monogram). */
  children?: React.ReactNode;
}

/**
 * Indeterminate loading ring: a faint full-circle track with a smooth
 * conic-gradient arc that fades along its circumference as it orbits.
 *
 * A circle is rotation-invariant, so only the arc appears to move — the
 * silhouette never wobbles the way a spinning rounded square does. Used both
 * standalone (page transitions) and wrapped around content (company selection).
 */
export function Spinner({ className, children }: SpinnerProps) {
  return (
    <div className={cn('relative grid place-items-center', className)}>
      {/* Faint full-circle track sits under the moving arc. */}
      <span className="absolute inset-0 rounded-full border border-border" />

      {/* The arc: a conic gradient masked into a thin ring, spun on transform. */}
      <span
        className="absolute inset-0 rounded-full motion-safe:animate-spin"
        style={{
          animationDuration: '1.3s',
          animationTimingFunction: 'linear',
          background: 'conic-gradient(transparent 60%, hsl(var(--primary)))',
          WebkitMask:
            'radial-gradient(farthest-side, transparent calc(100% - 2.5px), black calc(100% - 2px))',
          mask: 'radial-gradient(farthest-side, transparent calc(100% - 2.5px), black calc(100% - 2px))',
        }}
      />

      {children}
    </div>
  );
}
